import type { CallTransfer } from "api/voice";

// Where the card stands with a move of the live call's legs: a transfer
// (CRM-1747), a hold or an Add call (CRM-1818).
//
// Transfer now: the teammate's own leg ends the moment the call moves; the
// card keeps telling them how it went. "returning" waits for an unanswered
// call to ring back, which the softphone then answers on its own.
//
// Talk first and Add call: the caller waits on hold while the teammate, still
// on their own leg, speaks to the destination ("consult-*"), then completes,
// goes back or merges everyone into a 3-way call ("three-way"). Leave call
// ends the teammate's part in it ("left").
//
// Hold: the caller waits on hold music until Resume ("on-hold").
export type TransferPhase =
  | "transferring"
  | "returning"
  | "consult-ringing"
  | "consulting"
  | "consult-not-answered"
  | "consult-destination-left"
  | "transferred"
  | "caller-left"
  | "on-hold"
  | "three-way"
  // The caller hung up; the teammate and the added person are still on.
  | "three-way-caller-left"
  | "left";

// How a move ends while the teammate stays on the call: back to the normal
// card ("over"), or with a line saying why. "caller-hung-up-on-hold" ends the
// call too: nobody is left with the teammate.
export type TransferOutcome =
  | "over"
  | "caller-hung-up"
  | "caller-hung-up-on-hold"
  | "participant-left";

export const CONSULT_PHASES: TransferPhase[] = [
  "consult-ringing",
  "consulting",
  "consult-not-answered",
  "consult-destination-left"
];

export const THREE_WAY_PHASES: TransferPhase[] = [
  "three-way",
  "three-way-caller-left"
];

// The teammate is still on the call itself, not consulting or reading how a
// transfer went: the minimized pill can show it, and their leg dropping ends
// it.
export const ON_CALL_PHASES: TransferPhase[] = ["on-hold", ...THREE_WAY_PHASES];

// End call goes through the server here: a leg that just drops (a closed tab)
// instead lets a Talk first complete, strands a caller on hold or leaves the
// other two of a 3-way call talking.
export const SERVER_ENDED_PHASES: TransferPhase[] = [
  ...CONSULT_PHASES,
  ...ON_CALL_PHASES
];

// Phases the card keeps asking the server about.
export const POLLED_TRANSFER_PHASES: TransferPhase[] = [
  "transferring",
  ...SERVER_ENDED_PHASES
];

type TransferState = Pick<
  CallTransfer,
  "mode" | "status" | "caller_on_hold" | "call_ended" | "merged_at"
>;

const holdPhase = (latest: TransferState): TransferPhase | TransferOutcome => {
  // Resumed, or ended by a transfer that took over.
  if (latest.status !== "on_hold") return "over";
  return latest.call_ended ? "caller-hung-up-on-hold" : "on-hold";
};

// Talk first and Add call.
const parkedPhase = (
  latest: TransferState
): TransferPhase | TransferOutcome => {
  switch (latest.status) {
    // A merged one: the teammate left the 3-way call.
    case "answered":
      return latest.merged_at ? "left" : "transferred";
    case "merged":
      return latest.call_ended ? "three-way-caller-left" : "three-way";
    case "hung_up":
      return "participant-left";
    case "removed":
    case "ended":
      return "over";
    default:
      break;
  }
  if (latest.call_ended) return "caller-hung-up";
  // Gone back to the caller, or ended.
  if (!latest.caller_on_hold) return "over";
  if (latest.status === "consulting") return "consulting";
  if (latest.status === "not_answered") return "consult-not-answered";
  if (latest.status === "canceled") return "consult-destination-left";
  return "consult-ringing";
};

// The card's phase for what the server reports about a move it follows.
export const nextTransferPhase = (
  latest: TransferState
): TransferPhase | TransferOutcome => {
  if (latest.mode === "hold") return holdPhase(latest);
  if (latest.mode !== "now") return parkedPhase(latest);
  if (latest.status === "answered") return "transferred";
  if (latest.status === "canceled") return "caller-left";
  return latest.status === "ringing" ? "transferring" : "returning";
};
